import { installmentScheduleTypeConstants } from "constants/account";
import dayjs from "dayjs";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

import { IPaymentDetailsEdit } from "../PaymentDetails";

export const StyledPriceContainer = styled.div`
  p {
    color: #706f7b;
  }
  .highlighted {
    color: #17143f;
  }
`;

export const HorizontalLine = styled.hr`
  border-top: 1px solid #e2e2e4;
`;

export function PaymentPlanDetails({
  totalPatientPortion,
  order,
}: {
  totalPatientPortion: number | null;
  order?: IOrder;
}) {
  const {
    watch,
    formState: { errors },
  } = useFormContext<IPaymentDetailsEdit>();
  const paymentPlanMinDownPaymentFlatField = watch(
    "paymentPlanMinDownPaymentFlat"
  );
  const minDownPaymentTypeField = watch("paymentPlanMinDownPaymentType");
  const minDownPaymentPercentField = watch("paymentPlanMinDownPaymentPercent");
  const paymentPlanDurationField = watch("paymentPlanDuration");
  const paymentPlanDurationFullField = watch("paymentPlanDurationFull");

  const initialPayment = useMemo(() => {
    if (minDownPaymentTypeField === "Flat amount") {
      return dollarsToCents(paymentPlanMinDownPaymentFlatField || 0);
    }
    return (
      (totalPatientPortion || 0) * ((minDownPaymentPercentField || 0) / 100)
    );
  }, [
    totalPatientPortion,
    minDownPaymentPercentField,
    paymentPlanMinDownPaymentFlatField,
  ]);

  const monthlyPayment = useMemo(() => {
    const remainingAmount = (totalPatientPortion || 0) - initialPayment;
    return paymentPlanDurationField
      ? Math.floor((remainingAmount / paymentPlanDurationField) * 100) / 100
      : 0;
  }, [totalPatientPortion, initialPayment, paymentPlanDurationField]);

  const paymentsTimeline = useMemo(() => {
    const remainingAmount = (totalPatientPortion || 0) - initialPayment;
    const lastPayment = paymentPlanDurationField
      ? remainingAmount - monthlyPayment * (paymentPlanDurationField - 1)
      : [];
    const { installmentScheduleType } = order?.account || {};
    const isTwoWeekSchedule =
      installmentScheduleType ===
      installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;
    let startDate: dayjs.Dayjs;
    let addAMonth = true;
    if (isTwoWeekSchedule) {
      const twoWeeksBefore = dayjs
        .utc(order?.dateOfService)
        .subtract(14, "days");
      const now = dayjs.utc();
      startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
      addAMonth = !!twoWeeksBefore.isBefore(now);
    } else {
      startDate = dayjs();
    }

    const installments = Array.from(
      { length: paymentPlanDurationField || 0 },
      (_, index) => {
        const updatedIndex = addAMonth ? index + 1 : index;
        const date = startDate.add(updatedIndex, "month").format("YYYY-MM-DD");
        const payment =
          index === (paymentPlanDurationField || 0) - 1
            ? lastPayment
            : monthlyPayment;

        return { date, payment };
      }
    );
    const initialPaymentObj = {
      date: "Due Today",
      payment: initialPayment,
    };
    return [initialPaymentObj, ...installments];
  }, [
    totalPatientPortion,
    initialPayment,
    paymentPlanDurationField,
    order,
    monthlyPayment,
  ]);

  const formIsNotValid = useMemo(() => {
    if (
      paymentPlanDurationFullField === null ||
      paymentPlanDurationFullField === undefined
    ) {
      return true;
    }
    if (
      minDownPaymentTypeField === "Percentage" &&
      (minDownPaymentPercentField === null ||
        minDownPaymentPercentField === undefined)
    ) {
      return true;
    }
    if (
      minDownPaymentTypeField === "Flat amount" &&
      (paymentPlanMinDownPaymentFlatField === null ||
        paymentPlanMinDownPaymentFlatField === undefined)
    ) {
      return true;
    }
    return false;
  }, [
    paymentPlanDurationFullField,
    minDownPaymentTypeField,
    minDownPaymentPercentField,
    paymentPlanMinDownPaymentFlatField,
  ]);

  if (formIsNotValid) {
    return null;
  }
  return (
    <StyledPriceContainer className="flex flex-column gap-3 border-1 border-round-lg p-3 border-gray-300 mb-4">
      <div className="flex justify-content-between align-items-center w-full sm:flex-row flex-column gap-2">
        <p className="text-md highlighted font-semibold line-height-3">
          Due Today
        </p>
        <p className="text-lg text-primary font-semibold text-right line-height-1">
          {parsePrice(centsToDollars(initialPayment))}
        </p>
      </div>
      <HorizontalLine />
      <div className="flex justify-content-between align-items-center w-full sm:flex-row flex-column gap-3">
        <div className="flex flex-column gap-1 sm:align-items-start align-items-center">
          <p className="text-base font-medium line-height-3 highlighted">
            + {parsePrice(centsToDollars(monthlyPayment))} monthly payments{" "}
          </p>
          <p className="font-normal text-sm line-height-3">
            {paymentPlanDurationField} payments
          </p>
        </div>
        <div className="flex flex-column gap-1">
          <p className="font-normal text-sm line-height-3 flex align-items-center">
            <i
              className="pi pi-calendar text-primary"
              style={{ fontSize: "14px", marginRight: "4px" }}
            />
            Last payment
          </p>
          <p className="text-sm font-semibold line-height-3 text-primary text-right">
            {paymentsTimeline[paymentsTimeline.length - 1].date}
          </p>
        </div>
      </div>
      <HorizontalLine />
      <div className="flex justify-content-between align-items-center w-full sm:flex-row flex-column gap-2">
        <p className="font-normal text-sm line-height-3">No interest</p>
        <p className="font-normal text-sm line-height-3">
          Total Cost{" "}
          <span className="text-primary font-semibold text-md">
            {parsePrice(centsToDollars(totalPatientPortion || 0))}
          </span>
        </p>
      </div>
    </StyledPriceContainer>
  );
}
